import { Link } from 'wouter'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { SEOHead } from '@/components/seo-head'
import { createSoftwareAppStructuredData, SOURCEDIN_SOFTWARE_DATA } from '@/components/structured-data/software-app'
import { 
  Search, 
  Clock, 
  Shield, 
  BarChart3, 
  ArrowRight, 
  CheckCircle,
  DollarSign,
  Users,
  Zap
} from 'lucide-react'

export function Buyers() {
  const pageData = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "For Buyers - RFP Marketplace",
    "description": "Streamline your procurement process with SourcedIn. Find qualified vendors, reduce costs, and accelerate your RFP process with AI-powered matching."
  }

  // Create SoftwareApplication structured data
  const softwareAppData = createSoftwareAppStructuredData(SOURCEDIN_SOFTWARE_DATA)

  return (
    <div className="min-h-screen bg-white">
      <SEOHead
        title="For Buyers - Streamline Procurement | SourcedIn RFP Marketplace"
        description="Transform your procurement process with SourcedIn. Find qualified vendors faster, reduce costs by 40%, and accelerate RFPs with AI-powered vendor matching and evaluation tools."
        keywords="procurement platform, RFP buyers, vendor sourcing, procurement efficiency, supplier management, cost reduction, vendor evaluation"
        canonical="https://sourcedin.ai/buyers"
        structuredData={[pageData, softwareAppData]}
      />

      {/* Hero Section */}
      <section className="py-20 px-4 bg-gradient-to-br from-blue-50 to-indigo-50">
        <div className="max-w-6xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            Streamline Procurement for 
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600"> Buyers</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 max-w-4xl mx-auto leading-relaxed mb-8">
            Find qualified vendors faster, reduce procurement costs by 40%, 
            and make data-driven decisions with our intelligent RFP marketplace.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register">
              <Button size="lg" className="text-lg px-8 py-4 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700">
                Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/request-for-proposal-marketplace">
              <Button variant="outline" size="lg" className="text-lg px-8 py-4">
                Browse Vendors
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Key Benefits */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Why Buyers Choose SourcedIn
            </h2>
            <p className="text-xl text-gray-600">
              Transform your procurement process with proven results
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="h-8 w-8 text-blue-600" />
                </div>
                <CardTitle className="text-2xl">70% Faster</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  Reduce procurement cycles from months to weeks with automated 
                  vendor matching and streamlined workflows.
                </CardDescription>
                <div className="space-y-2">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Automated matching</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Streamlined evaluation</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="h-8 w-8 text-green-600" />
                </div>
                <CardTitle className="text-2xl">40% Cost Savings</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  Achieve significant cost reductions through competitive bidding 
                  and access to a global vendor network.
                </CardDescription>
                <div className="space-y-2">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Competitive pricing</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Global vendor access</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="border-0 shadow-lg hover:shadow-xl transition-shadow text-center">
              <CardHeader className="pb-4">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-purple-600" />
                </div>
                <CardTitle className="text-2xl">5x More Vendors</CardTitle>
              </CardHeader>
              <CardContent>
                <CardDescription className="text-base mb-4">
                  Access a network of 50,000+ pre-qualified vendors across 
                  all industries and specializations.
                </CardDescription>
                <div className="space-y-2">
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Pre-qualified vendors</span>
                  </div>
                  <div className="flex items-center justify-center space-x-2">
                    <CheckCircle className="h-4 w-4 text-green-600" />
                    <span className="text-sm">Global coverage</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Success Metrics */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Proven Results for Buyers
            </h2>
            <p className="text-xl text-gray-600">
              Real outcomes from procurement teams using SourcedIn
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 mb-16">
            <div className="text-center">
              <div className="text-4xl font-bold text-blue-600 mb-2">70%</div>
              <div className="text-gray-600">Faster Procurement</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-green-600 mb-2">40%</div>
              <div className="text-gray-600">Cost Reduction</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-purple-600 mb-2">95%</div>
              <div className="text-gray-600">Success Rate</div>
            </div>
            <div className="text-center">
              <div className="text-4xl font-bold text-orange-600 mb-2">5x</div>
              <div className="text-gray-600">More Vendor Options</div>
            </div>
          </div>

          <Card className="border-0 shadow-lg p-8">
            <blockquote className="text-center">
              <p className="text-2xl font-light text-gray-700 mb-6 italic">
                "SourcedIn revolutionized our procurement process. We reduced vendor sourcing time by 60% 
                and found higher quality suppliers than ever before."
              </p>
              <footer className="text-lg">
                <strong>Sarah Johnson</strong>
                <br />
                <span className="text-gray-600">Chief Procurement Officer, TechCorp Industries</span>
              </footer>
            </blockquote>
          </Card>
        </div>
      </section>

      {/* Buyer Process */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              How It Works for Buyers
            </h2>
            <p className="text-xl text-gray-600">
              Streamlined procurement in 4 simple steps
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-xl font-bold">
                1
              </div>
              <h3 className="text-xl font-bold mb-4">Create RFP</h3>
              <p className="text-gray-600">
                Use our guided builder to create professional RFPs with industry best practices.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-xl font-bold">
                2
              </div>
              <h3 className="text-xl font-bold mb-4">Get Matched</h3>
              <p className="text-gray-600">
                Our AI instantly matches your RFP with the most qualified vendors in our network.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-purple-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-xl font-bold">
                3
              </div>
              <h3 className="text-xl font-bold mb-4">Evaluate</h3>
              <p className="text-gray-600">
                Compare proposals side-by-side with our evaluation tools and vendor ratings.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-orange-600 rounded-full flex items-center justify-center mx-auto mb-6 text-white text-xl font-bold">
                4
              </div>
              <h3 className="text-xl font-bold mb-4">Select & Close</h3>
              <p className="text-gray-600">
                Choose the best vendor and close deals faster with built-in communication tools.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Buyer Features */}
      <section className="py-20 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Buyer-Focused Features
            </h2>
            <p className="text-xl text-gray-600">
              Tools designed specifically for procurement professionals
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            <Card className="p-8 border-0 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Search className="h-6 w-6 text-blue-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Smart Vendor Discovery</h3>
                  <p className="text-gray-600 mb-4">
                    Find the perfect vendors using AI-powered matching based on expertise, 
                    location, budget, and past performance.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">AI-powered recommendations</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Advanced filtering</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-8 border-0 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="h-6 w-6 text-green-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Procurement Analytics</h3>
                  <p className="text-gray-600 mb-4">
                    Make data-driven decisions with comprehensive analytics on spend, 
                    vendor performance, and market trends.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Spend analysis</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Performance tracking</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-8 border-0 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Shield className="h-6 w-6 text-purple-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Compliance Management</h3>
                  <p className="text-gray-600 mb-4">
                    Ensure vendor compliance with automated tracking of certifications, 
                    insurance, and regulatory requirements.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Automated compliance checks</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Document management</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>

            <Card className="p-8 border-0 shadow-lg">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Zap className="h-6 w-6 text-orange-600" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-3">Workflow Automation</h3>
                  <p className="text-gray-600 mb-4">
                    Streamline your procurement process with custom workflows, 
                    approval chains, and automated notifications.
                  </p>
                  <div className="space-y-2">
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Custom approval flows</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <CheckCircle className="h-4 w-4 text-green-600" />
                      <span className="text-sm">Automated notifications</span>
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
        </div>
      </section>

      {/* Industry Focus */}
      <section className="py-20 px-4">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              Trusted by Industry Leaders
            </h2>
            <p className="text-xl text-gray-600">
              Procurement teams across all industries rely on SourcedIn
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <Card className="text-center border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">Healthcare</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  500+ healthcare organizations streamline medical equipment and service procurement.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">Technology</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  1,200+ tech companies find IT services and software development partners.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-xl">Manufacturing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600">
                  800+ manufacturers source materials, equipment, and specialized services.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 bg-gradient-to-r from-blue-600 to-indigo-600 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold mb-6">
            Transform Your Procurement Today
          </h2>
          <p className="text-xl mb-8 opacity-90">
            Join thousands of buyers who have revolutionized their procurement process with SourcedIn.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link href="/register">
              <Button size="lg" variant="secondary" className="text-lg px-8 py-4">
                Start Free Trial <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
            <Link href="/request-for-proposal-marketplace">
              <Button size="lg" variant="outline" className="text-lg px-8 py-4 border-white text-white hover:bg-white hover:text-blue-600">
                Browse Vendors
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
